/**
 * Generic entity table with pagination, search, and edition filtering
 */

import { useState, useMemo } from 'react'
import { Search, ChevronLeft, ChevronRight, ArrowUpDown, Calendar, Download } from 'lucide-react'
import { useEntityList, useEditionStats } from '../hooks/queries'
import type { EntityType, EntityLevel } from '../types'
import { ENTITY_LABELS } from '../types'
import { cn, formatNumber, formatArea } from '../../lib/utils'
import { EntityPageTabs, type EntityPageTab } from '../components/EntityPageTabs'
import { DataSourcesView } from '../components/DataSourcesView'

// Mapping from EntityType (URL slug) to EntityLevel (API level)
const ENTITY_TYPE_TO_LEVEL: Record<EntityType, EntityLevel> = {
  states: 'state',
  'government-districts': 'government_district',
  counties: 'county',
  'municipal-associations': 'municipal_association',
  municipalities: 'municipality',
}

interface Column {
  key: string
  label: string
  sortable?: boolean
  format?: (value: unknown) => string
  className?: string
}

const COLUMNS_BY_TYPE: Record<EntityType, Column[]> = {
  states: [
    { key: 'code', label: 'Code', sortable: true },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'designation', label: 'Bezeichnung' },
    { key: 'capital', label: 'Hauptstadt' },
    { key: 'population', label: 'Einwohner', format: formatNumber, className: 'text-right' },
    { key: 'area_km2', label: 'Fläche', format: formatArea, className: 'text-right' },
    { key: 'county_count', label: 'Kreise', format: formatNumber, className: 'text-right' },
  ],
  'government-districts': [
    { key: 'ags', label: 'AGS', sortable: true },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'designation', label: 'Bezeichnung' },
    { key: 'state_name', label: 'Bundesland' },
    { key: 'administrative_seat', label: 'Verwaltungssitz' },
  ],
  counties: [
    { key: 'ags', label: 'AGS', sortable: true },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'designation', label: 'Bezeichnung' },
    { key: 'textkennzeichen', label: 'TK' },
    { key: 'state_name', label: 'Bundesland' },
    { key: 'population', label: 'Einwohner', format: formatNumber, className: 'text-right' },
    { key: 'area_km2', label: 'Fläche', format: formatArea, className: 'text-right' },
    { key: 'municipality_count', label: 'Gemeinden', format: formatNumber, className: 'text-right' },
  ],
  'municipal-associations': [
    { key: 'ars', label: 'ARS', sortable: true },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'designation', label: 'Bezeichnung' },
    { key: 'textkennzeichen', label: 'TK' },
    { key: 'county_name', label: 'Kreis' },
    { key: 'population', label: 'Einwohner', format: formatNumber, className: 'text-right' },
    { key: 'area_km2', label: 'Fläche', format: formatArea, className: 'text-right' },
    { key: 'municipality_count', label: 'Gemeinden', format: formatNumber, className: 'text-right' },
  ],
  municipalities: [
    { key: 'ags', label: 'AGS', sortable: true },
    { key: 'ars', label: 'ARS' },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'designation', label: 'Bezeichnung' },
    { key: 'name_addon', label: 'Zusatz' },
    { key: 'textkennzeichen', label: 'TK' },
    { key: 'county_name', label: 'Kreis' },
    { key: 'population', label: 'Einwohner', format: (v) => formatNumber(v, { fallback: '-' }), className: 'text-right' },
    { key: 'area_km2', label: 'Fläche', format: formatArea, className: 'text-right' },
  ],
}

interface EntityTableProps {
  entityType: EntityType
}

export function EntityTable({ entityType }: EntityTableProps) {
  const [activeTab, setActiveTab] = useState<EntityPageTab>('table')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(0)
  // undefined = not yet selected (use default), null = explicitly "all editions"
  const [selectedEditionId, setSelectedEditionId] = useState<number | null | undefined>(undefined)
  const limit = 50

  const entityLevel = ENTITY_TYPE_TO_LEVEL[entityType]

  // Load available editions
  const { data: editionData } = useEditionStats()

  // Default to latest edition when data loads, but respect explicit null (all editions)
  const effectiveEditionId = selectedEditionId === undefined
    ? editionData?.selected_edition_id ?? null
    : selectedEditionId  // null means "all editions" was explicitly selected

  // Debounce search
  useMemo(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(0) // Reset to first page on search
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const { data, isLoading, error } = useEntityList(entityType, {
    limit,
    offset: page * limit,
    search: debouncedSearch,
    editionId: effectiveEditionId,
  })

  const columns = COLUMNS_BY_TYPE[entityType]
  const label = ENTITY_LABELS[entityType]

  const totalPages = data ? Math.ceil(data.total / limit) : 0

  // Get the selected edition info for display
  const selectedEdition = editionData?.editions?.find(e => e.id === effectiveEditionId)

  // Handle edition change
  const handleEditionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setSelectedEditionId(value ? parseInt(value, 10) : null)
    setPage(0) // Reset to first page on edition change
  }

  return (
    <div className="space-y-6">
      {/* Header with Edition Selector */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{label}</h1>
          <div className="flex items-center gap-2 mt-1">
            {activeTab === 'table' && data && (
              <span className="text-gray-500">
                {data.total.toLocaleString('de-DE')} Einträge
              </span>
            )}
            {activeTab === 'table' && selectedEdition && (
              <span className="text-sm text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                Gebietsstand: {selectedEdition.reference_date_display}
              </span>
            )}
          </div>
        </div>

        {/* Controls (only in table view) */}
        {activeTab === 'table' && (
          <div className="flex items-center gap-4">
            {/* Edition Selector */}
            {editionData?.editions && editionData.editions.length > 0 && (
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-400" />
                <select
                  value={effectiveEditionId ?? ''}
                  onChange={handleEditionChange}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Alle Editionen</option>
                  {editionData.editions.map((edition) => (
                    <option key={edition.id} value={edition.id}>
                      Edition {edition.edition} ({edition.reference_date_display})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Export Button */}
            <a
              href={`${import.meta.env.VITE_API_URL || 'http://localhost:8899'}/admin/entities/${entityType}/export`}
              download
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              CSV Export
            </a>
          </div>
        )}
      </div>

      {/* Tabs */}
      <EntityPageTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab Content */}
      {activeTab === 'table' ? (
        <>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Table with horizontal scroll */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
              </div>
            ) : error ? (
              <div className="p-4 text-red-600">Fehler beim Laden der Daten</div>
            ) : (
              <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                <table className="w-full min-w-max">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {columns.map((col) => (
                        <th
                          key={col.key}
                          className={cn(
                            'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap',
                            col.className
                          )}
                        >
                          <div className="flex items-center gap-1">
                            {col.label}
                            {col.sortable && (
                              <ArrowUpDown className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data?.results.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        {columns.map((col) => {
                          const value = row[col.key as keyof typeof row]
                          const formatted = col.format ? col.format(value) : (value ?? '-') as React.ReactNode
                          return (
                            <td
                              key={col.key}
                              className={cn(
                                'px-4 py-3 text-sm text-gray-900 whitespace-nowrap',
                                col.className
                              )}
                            >
                              {formatted}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                    {data?.results.length === 0 && (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="px-4 py-8 text-center text-gray-500"
                        >
                          Keine Einträge gefunden
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {data && totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Seite {page + 1} von {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className={cn(
                    'p-2 rounded-lg border transition-colors',
                    page === 0
                      ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  )}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className={cn(
                    'p-2 rounded-lg border transition-colors',
                    page >= totalPages - 1
                      ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  )}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <DataSourcesView entityLevel={entityLevel} />
      )}
    </div>
  )
}
